import type { FunctionReturnType } from "convex/server";
import type { api } from "@convex/_generated/api";
import type { Tone } from "@/components/ui/tone";
import { formatCentsWithCurrency } from "@/lib/formatting/currency";
import { regionCodeForPostalCode } from "@shared/formats/postal-region";
import { POSTAL_CODE_FORMAT } from "@shared/formats/postal-code";

export type PriceCheckResult = FunctionReturnType<typeof api.priceIndex.checkPrice.checkPrice>;

export type CheckSide = "buy" | "sell";

export type Verdict = "low" | "fair" | "high";

export type MarketBand = {
  medianCents: number;
  lowCents: number;
  highCents: number;
  minCents: number;
  maxCents: number;
  currency: "USD" | "CAD";
  placeCode: string | null;
};

export const SIDE_OPTIONS: Array<{ value: CheckSide; label: string }> = [
  { value: "buy", label: "Buying" },
  { value: "sell", label: "Selling" },
];

export const VERDICT_TAG: Record<Verdict, string> = {
  low: "Below typical",
  fair: "Typical",
  high: "Above typical",
};

const FAVOURABLE: Record<CheckSide, Verdict> = { buy: "low", sell: "high" };

export function verdictTone(verdict: Verdict, side: CheckSide): Tone {
  if (verdict === "fair") return "info";
  return verdict === FAVOURABLE[side] ? "success" : "warning";
}

const PRICE_FORMAT = /^\$?(\d{1,3}(,\d{3}){1,2}|\d{1,9})(\.\d{1,2})?$/;

const IMPLAUSIBLE_PRICE_FACTOR = 10;

export function parsePriceCents(value: string): number | null {
  const trimmed = value.trim();
  if (!PRICE_FORMAT.test(trimmed)) return null;
  const cents = Math.round(Number(trimmed.replace(/[$,]/g, "")) * 100);
  return cents >= 1 ? cents : null;
}

export function priceInputError(value: string): string | null {
  if (value.trim() === "" || parsePriceCents(value) !== null) return null;
  return "Enter a price in dollars, like 4.96.";
}

export function isImplausiblePrice(band: MarketBand, priceCents: number): boolean {
  if (band.medianCents <= 0) return false;
  return (
    priceCents > band.medianCents * IMPLAUSIBLE_PRICE_FACTOR ||
    priceCents * IMPLAUSIBLE_PRICE_FACTOR < band.medianCents
  );
}

export function centsToInput(cents: number): string {
  return (cents / 100).toFixed(2);
}

export function checkablePostalCode(raw: string): string | undefined {
  const normalized = POSTAL_CODE_FORMAT.normalize(raw);
  return normalized && regionCodeForPostalCode(normalized) ? normalized : undefined;
}

export function marketBand(result: PriceCheckResult | undefined): MarketBand | null {
  if (!result) return null;
  const {
    marketMedianCents,
    marketLowerQuartileCents,
    marketUpperQuartileCents,
    marketMinCents,
    marketMaxCents,
  } = result;
  if (
    marketMedianCents === undefined ||
    marketLowerQuartileCents === undefined ||
    marketUpperQuartileCents === undefined ||
    marketMinCents === undefined ||
    marketMaxCents === undefined
  ) {
    return null;
  }
  return {
    medianCents: marketMedianCents,
    lowCents: marketLowerQuartileCents,
    highCents: marketUpperQuartileCents,
    minCents: marketMinCents,
    maxCents: marketMaxCents,
    currency: result.currency ?? "USD",
    placeCode: result.place?.code ?? null,
  };
}

export function verdictFor(band: MarketBand, priceCents: number): Verdict {
  if (priceCents < band.lowCents) return "low";
  if (priceCents > band.highCents) return "high";
  return "fair";
}

export function percentFromMedian(band: MarketBand, priceCents: number): number {
  return band.medianCents > 0
    ? Math.round(((priceCents - band.medianCents) / band.medianCents) * 100)
    : 0;
}

export function rulerBounds(band: MarketBand): { minCents: number; maxCents: number } {
  const spread = Math.max(band.maxCents - band.minCents, band.medianCents * 0.2, 100);
  return {
    minCents: Math.max(1, Math.floor((band.minCents - spread * 0.25) / 5) * 5),
    maxCents: Math.ceil((band.maxCents + spread * 0.25) / 5) * 5,
  };
}

export function formatMoney(cents: number, currency: MarketBand["currency"]): string {
  return formatCentsWithCurrency(cents, currency);
}

export function scopeName(band: MarketBand): string {
  return band.placeCode ?? "US";
}

export function medianComparison(band: MarketBand, priceCents: number): string {
  const percent = percentFromMedian(band, priceCents);
  const where = `the ${scopeName(band)} median`;
  if (percent > 0) return `${percent}% over ${where}`;
  if (percent < 0) return `${-percent}% under ${where}`;
  return `Right at ${where}`;
}

export function verdictHeadline(
  band: MarketBand,
  priceCents: number,
  side: CheckSide,
): string {
  const verdict = verdictFor(band, priceCents);
  const percent = Math.abs(percentFromMedian(band, priceCents));
  const where = `the ${scopeName(band)} median`;
  if (side === "buy") {
    if (verdict === "high") return `You're paying ${percent}% over ${where}`;
    if (verdict === "low") return "That's a strong price";
    return "You're paying about what the market pays";
  }
  if (verdict === "low") return `You're being offered ${percent}% under ${where}`;
  if (verdict === "high") return "That's a strong offer";
  return "That's a fair offer";
}

export function yearlyDifferenceCents(
  band: MarketBand,
  priceCents: number,
  unitsPerLoad: number,
  loadsPerMonth: number,
): number {
  return (priceCents - band.medianCents) * unitsPerLoad * loadsPerMonth * 12;
}

export function wholeNumber(value: string): number {
  const parsed = Math.floor(Number(value));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}
